import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Download,
  RefreshCw,
  Database,
  Kanban
} from 'lucide-react';

interface ValidationResult {
  totalCSVRows: number;
  totalTitulosInDB: number;
  totalCardsInKanban: number;
  totalTitulosInKanban: number;
  discrepancies: {
    missingTitulos: string[];
    extraTitulos: string[];
    statusMismatch: Array<{
      numero: string;
      csvStatus: string;
      dbStatus: string;
    }>;
    valueMismatch: Array<{
      numero: string;
      csvValue: string;
      dbValue: string;
    }>;
  };
  summary: {
    isConsistent: boolean;
    issues: string[];
  };
}

export const KanbanValidationPage: React.FC = () => {
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [csvFilePath, setCsvFilePath] = useState('');

  const handleValidate = async () => {
    try {
      setIsValidating(true);
      
      const response = await api.post('/titulos/validate-kanban', {
        csvFilePath: csvFilePath || undefined
      });
      
      setValidationResult(response.data.data);
      
      if (response.data.data.summary.isConsistent) {
        toast.success('Dados do Kanban estão consistentes!');
      } else {
        toast.error(`Encontradas ${response.data.data.summary.issues.length} inconsistências`);
      }
    } catch (error: any) {
      console.error('Erro na validação:', error);
      toast.error(error.response?.data?.message || 'Erro ao validar dados do Kanban');
    } finally {
      setIsValidating(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setIsGeneratingReport(true);
      
      const response = await api.post('/titulos/generate-validation-report', {
        csvFilePath: csvFilePath || undefined
      });
      
      // Criar e baixar o relatório
      const blob = new Blob([response.data.data.report], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kanban-validation-report-${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Relatório gerado e baixado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(error.response?.data?.message || 'Erro ao gerar relatório');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const getStatusIcon = (isConsistent: boolean) => {
    return isConsistent ? (
      <CheckCircle className="h-6 w-6 text-green-500" />
    ) : (
      <XCircle className="h-6 w-6 text-red-500" />
    );
  };

  const getStatusBadge = (isConsistent: boolean) => {
    return isConsistent ? (
      <Badge className="bg-green-100 text-green-800">Consistente</Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800">Inconsistente</Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Validação dos Dados do Kanban</h1>
          <p className="text-muted-foreground mt-1">
            Verifique se os dados do kanban estão de acordo com o arquivo importado
          </p>
        </div>
      </div>

      {/* Controles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Controles de Validação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground text-foreground mb-2">
              Caminho do arquivo CSV (opcional)
            </label>
            <input
              type="text"
              value={csvFilePath}
              onChange={(e) => setCsvFilePath(e.target.value)}
              placeholder="Ex: /path/to/file.csv"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Deixe vazio para validar apenas os dados do banco
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleValidate}
              disabled={isValidating}
              className="flex items-center gap-2"
            >
              {isValidating ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              {isValidating ? 'Validando...' : 'Validar Dados'}
            </Button>
            
            <Button
              onClick={handleGenerateReport}
              disabled={isGeneratingReport || !validationResult}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isGeneratingReport ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isGeneratingReport ? 'Gerando...' : 'Gerar Relatório'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultados */}
      {validationResult && (
        <div className="space-y-6">
          {/* Resumo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getStatusIcon(validationResult.summary.isConsistent)}
                Resumo da Validação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/40 rounded-lg">
                  <Database className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <div className="text-2xl font-bold text-blue-600">
                    {validationResult.totalTitulosInDB}
                  </div>
                  <div className="text-sm text-blue-600">Títulos no Banco</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Kanban className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <div className="text-2xl font-bold text-green-600">
                    {validationResult.totalCardsInKanban}
                  </div>
                  <div className="text-sm text-green-600">Cards no Kanban</div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <FileText className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                  <div className="text-2xl font-bold text-purple-600">
                    {validationResult.totalCSVRows}
                  </div>
                  <div className="text-sm text-purple-600">Linhas no CSV</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <AlertTriangle className="h-8 w-8 mx-auto text-orange-500 mb-2" />
                  <div className="text-2xl font-bold text-orange-600">
                    {validationResult.summary.issues.length}
                  </div>
                  <div className="text-sm text-orange-600">Issues Encontradas</div>
                </div>
              </div>
              
              <div className="mt-4 flex items-center gap-2">
                <span className="text-sm font-medium">Status:</span>
                {getStatusBadge(validationResult.summary.isConsistent)}
              </div>
            </CardContent>
          </Card>

          {/* Discrepâncias */}
          {!validationResult.summary.isConsistent && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Discrepâncias Encontradas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Títulos Faltantes */}
                {validationResult.discrepancies.missingTitulos.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-600 mb-2">
                      Títulos Faltantes no Banco ({validationResult.discrepancies.missingTitulos.length})
                    </h4>
                    <div className="bg-red-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                      {validationResult.discrepancies.missingTitulos.map((titulo, index) => (
                        <div key={index} className="text-sm text-red-700">
                          • {titulo}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Títulos Extras */}
                {validationResult.discrepancies.extraTitulos.length > 0 && (
                  <div>
                    <h4 className="font-medium text-blue-600 mb-2">
                      Títulos Extras no Banco ({validationResult.discrepancies.extraTitulos.length})
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-lg max-h-40 overflow-y-auto">
                      {validationResult.discrepancies.extraTitulos.map((titulo, index) => (
                        <div key={index} className="text-sm text-blue-700">
                          • {titulo}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inconsistências de Status */}
                {validationResult.discrepancies.statusMismatch.length > 0 && (
                  <div>
                    <h4 className="font-medium text-orange-600 mb-2">
                      Inconsistências de Status ({validationResult.discrepancies.statusMismatch.length})
                    </h4>
                    <div className="bg-orange-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                      {validationResult.discrepancies.statusMismatch.map((item, index) => (
                        <div key={index} className="text-sm text-orange-700">
                          • {item.numero}: CSV={item.csvStatus}, DB={item.dbStatus}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inconsistências de Valor */}
                {validationResult.discrepancies.valueMismatch.length > 0 && (
                  <div>
                    <h4 className="font-medium text-purple-600 mb-2">
                      Inconsistências de Valor ({validationResult.discrepancies.valueMismatch.length})
                    </h4>
                    <div className="bg-purple-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                      {validationResult.discrepancies.valueMismatch.map((item, index) => (
                        <div key={index} className="text-sm text-purple-700">
                          • {item.numero}: CSV={item.csvValue}, DB={item.dbValue}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Issues */}
          {validationResult.summary.issues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Issues Identificadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {validationResult.summary.issues.map((issue, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{issue}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
